/**
 * Theme core — single source of truth for color mode.
 *
 * Pure module, no React. Owns:
 *   - Persistent ThemeMode in localStorage ("light" | "dark" | "system")
 *   - The `.dark` class on <html>
 *   - The crossfade window via `data-theme-transition` attribute
 *   - Cross-tab sync via `storage` event
 *   - System pref reactivity when mode === "system"
 *
 * Timing is CSS-driven: --theme-transition-duration (declared in globals.css)
 * is read via getComputedStyle so JS and CSS never disagree.
 *
 * Re-exported under `useTheme()` for React consumers; existing callers of
 * `useDarkModeSync()` keep working via a thin shim.
 */

export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "theme";
const DARK_CLASS = "dark";
const TRANSITION_ATTR = "data-theme-transition";
const DURATION_VAR = "--theme-transition-duration";
const DURATION_FALLBACK_MS = 300;

const listeners = new Set<() => void>();
const isBrowser = typeof window !== "undefined";

const systemDarkMq = isBrowser
  ? window.matchMedia("(prefers-color-scheme: dark)")
  : null;

function emit(): void {
  for (const cb of listeners) cb();
}

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Read the persisted mode. Migrates the legacy JSON-boolean format on the fly. */
export function getMode(): ThemeMode {
  if (!isBrowser) return "system";
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return "system";
    if (raw === "true") return "dark";
    if (raw === "false") return "light";
    const parsed = JSON.parse(raw) as ThemeMode;
    return parsed === "dark" || parsed === "light" ? parsed : "system";
  } catch {
    return "system";
  }
}

/** Resolve mode to a concrete dark/light boolean (handles "system"). */
export function resolveIsDark(mode: ThemeMode = getMode()): boolean {
  if (mode === "dark") return true;
  if (mode === "light") return false;
  return systemDarkMq?.matches ?? false;
}

function prefersReducedMotion(): boolean {
  return isBrowser && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function readDurationMs(): number {
  if (!isBrowser) return DURATION_FALLBACK_MS;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(DURATION_VAR).trim();
  if (!raw) return DURATION_FALLBACK_MS;
  if (raw.endsWith("ms")) return parseFloat(raw) || DURATION_FALLBACK_MS;
  if (raw.endsWith("s")) return (parseFloat(raw) || 0) * 1000 || DURATION_FALLBACK_MS;
  return parseFloat(raw) || DURATION_FALLBACK_MS;
}

interface ApplyOptions {
  /** Animate the swap (default true). Set false for first paint / cross-tab passive updates. */
  transition?: boolean;
  /** Persist to localStorage (default true). */
  persist?: boolean;
}

/**
 * Apply a theme mode. Single entry point for every theme change.
 *
 * Flow: persist → (optional) arm crossfade → flip `.dark` class → notify listeners → unarm.
 */
export function applyTheme(mode: ThemeMode, opts: ApplyOptions = {}): void {
  if (!isBrowser) return;
  const { transition = true, persist = true } = opts;
  const root = document.documentElement;
  const isDark = resolveIsDark(mode);

  const commit = (): void => {
    root.classList.toggle(DARK_CLASS, isDark);
    if (persist) localStorage.setItem(STORAGE_KEY, JSON.stringify(mode));
    emit();
  };

  if (!transition || prefersReducedMotion()) {
    commit();
    return;
  }

  const durationMs = readDurationMs();
  root.setAttribute(TRANSITION_ATTR, "");
  // One paint frame so the browser registers the transition properties before
  // CSS custom properties change. Without this, the swap looks instant.
  requestAnimationFrame(() => {
    commit();
    window.setTimeout(() => root.removeAttribute(TRANSITION_ATTR), durationMs + 60);
  });
}

/**
 * Smart 3-state cycle (Light → Dark → System) — ported from the portfolio toggle.
 *
 * Ordered so the *first* click from the default "system" state always flips the
 * visual theme. Pure: pass the current system preference so it stays unit-testable.
 */
export function cycleTheme(current: ThemeMode, systemDark: boolean): ThemeMode {
  if (current === "system") return systemDark ? "light" : "dark";
  if (current === "light") return systemDark ? "dark" : "system";
  return systemDark ? "system" : "light"; // current === "dark"
}

/** Origin (viewport px) for the wipe's expanding circle — usually a control's center. */
export interface WipeOrigin {
  x: number;
  y: number;
}

/**
 * Apply a theme with the circular page wipe (View Transitions API).
 *
 * Flow: startViewTransition snapshots the page → applyTheme flips `.dark` with its
 * own crossfade disabled → the CSS `themeWipe` keyframes reveal the new theme as an
 * expanding circle. Falls back to a plain crossfade when the API is unavailable or
 * reduced-motion is requested.
 *
 * Origin: when `origin` is given (the clicked control's center), the circle starts
 * there and its radius is computed to reach the farthest viewport corner. Without
 * it, the dir-aware corner origin from CSS applies.
 *
 * Ban #4 note: a per-click pixel origin cannot be a static CSS class, so it is
 * written as custom properties on <html>. This is the one centralized exception,
 * kept in the theme module which already owns every theme-related DOM write.
 */
export function applyThemeWithWipe(mode: ThemeMode, origin?: WipeOrigin): void {
  if (!isBrowser) return;
  const doc = document as Document & {
    startViewTransition?: (cb: () => void) => unknown;
  };
  if (prefersReducedMotion() || typeof doc.startViewTransition !== "function") {
    applyTheme(mode);
    return;
  }

  const root = document.documentElement;
  if (origin) {
    // Radius reaches the farthest viewport corner so the circle fully covers the screen.
    const r = Math.hypot(
      Math.max(origin.x, window.innerWidth - origin.x),
      Math.max(origin.y, window.innerHeight - origin.y),
    );
    root.style.setProperty("--theme-x", `${origin.x}px`);
    root.style.setProperty("--theme-y", `${origin.y}px`);
    root.style.setProperty("--theme-r", `${r}px`);
  } else {
    // Drop any prior inline origin so the CSS corner default applies again.
    root.style.removeProperty("--theme-x");
    root.style.removeProperty("--theme-y");
    root.style.removeProperty("--theme-r");
  }

  doc.startViewTransition(() => applyTheme(mode, { transition: false }));
}

/** First-paint init. Called once at module load (below) so the class is set before React mounts. */
function init(): void {
  if (!isBrowser) return;
  applyTheme(getMode(), { transition: false, persist: false });

  // Cross-tab sync: another tab wrote to localStorage.
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    applyTheme(getMode(), { persist: false });
  });

  // Custom intra-tab dispatch fired by writes to localStorage in this tab —
  // matches the pattern used by useSyncSettings for language sync.
  window.addEventListener("theme:change", () => {
    applyTheme(getMode(), { persist: false });
  });

  // System preference change — re-resolve only when mode === "system".
  systemDarkMq?.addEventListener("change", () => {
    if (getMode() === "system") applyTheme("system", { persist: false });
  });
}

init();
